import React from 'react'
import useSWR from 'swr'
import { fetchBlacklists, createBlacklist, deleteBlacklist } from '../api'
import Client from '../../models/client'
import { ActionIcon, Box, Button, Center, Group, Loader, Table, Text, TextInput, ThemeIcon, Title, Tooltip } from '@mantine/core'
import { GearIcon, TrashIcon } from '@phosphor-icons/react'
import { notifications } from '@mantine/notifications'
import { useForm } from '@mantine/form'
import { confirmDelete } from '../utils/confirm'

const resolveAddErrorMessage = (e: unknown): string => {
  const serverError = (e as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0]
  if (serverError?.includes('has already been taken')) {
    return '同じ紹介者ID/コードがすでに登録されています'
  }
  return '追加に失敗しました'
}

interface Props {
  client: Client
}

interface BlacklistItem {
  id: number
  suid?: string
  code?: string
  name?: string
  reason?: string
}

const BlacklistContainer: React.FC<Props> = ({ client }) => {
  const clientId = client.id
  const { data, mutate } = useSWR(`/admin/clients/${clientId}/blacklists`, () => fetchBlacklists(clientId))

  const form = useForm({
    initialValues: { suid: '', code: '', reason: '' },
    validate: (values) => {
      const errors: Record<string, string> = {}
      if (!values.suid && !values.code) {
        errors.suid = '紹介者IDまたは紹介者コードを入力してください'
      } else if (values.suid && values.code) {
        errors.suid = '紹介者IDと紹介者コードは同時に指定できません'
      } else if (values.suid && !/^[1-9]\d*$/.test(values.suid)) {
        errors.suid = '紹介者IDは1以上の数字で入力してください'
      }
      return errors
    }
  })

  const handleAdd = async (values: typeof form.values) => {
    try {
      await createBlacklist(clientId, {
        suid: values.suid || undefined,
        code: values.code || undefined,
        reason: values.reason || undefined,
      })
      form.reset()
      await mutate()
      notifications.show({ message: 'ブラックリストに追加しました', color: 'teal' })
    } catch (e) {
      console.error('ブラックリストの追加に失敗しました', e)
      notifications.show({ message: resolveAddErrorMessage(e), color: 'red' })
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirmDelete('ブラックリスト')) return
    try {
      await deleteBlacklist(clientId, id)
      await mutate()
      notifications.show({ message: 'ブラックリストを削除しました', color: 'teal' })
    } catch (e) {
      console.error('ブラックリストの削除に失敗しました', e)
      notifications.show({ message: '削除に失敗しました', color: 'red' })
    }
  }

  if (!data) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  return (
    <Box>
      <Group gap={12} mb={24}>
        <ThemeIcon variant="transparent" color="#4BC1C7">
          <GearIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          ブラックリスト
        </Title>
      </Group>

      <Text fz={14} c="#000000CC">
        SUIDまたはcodeを指定してユーザーをブロックできます。
      </Text>
      <Box style={{ backgroundColor: '#00000008', borderRadius: 8, padding: 24, marginTop: 24, marginBottom: 32 }}>
        <form onSubmit={form.onSubmit(handleAdd)}>
          <Group grow align="flex-start" gap={16}>
            <TextInput
              label="紹介者ID（suid）"
              {...form.getInputProps('suid')}
            />
            <TextInput
              label="紹介者コード（code）"
              {...form.getInputProps('code')}
            />
            <TextInput
              label="理由"
              {...form.getInputProps('reason')}
            />
          </Group>
          <Group justify="flex-end" mt={16}>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
            >
              追加
            </Button>
          </Group>
        </form>
      </Box>
      <Box style={{ border: '1px solid #0000001A', borderRadius: 8, overflow: 'hidden' }}>
        <Table withColumnBorders styles={{ th: { backgroundColor: '#00000008' } }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>紹介者ID（suid）</Table.Th>
              <Table.Th>紹介者コード（code）</Table.Th>
              <Table.Th>紹介者名（name）</Table.Th>
              <Table.Th>理由</Table.Th>
              <Table.Th w={80} ta="center">削除</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="#00000099" ta="center" py="md">
                    ブラックリストが登録されていません
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              data.map((b: BlacklistItem) => (
                <Table.Tr key={b.id}>
                  <Table.Td>{b.id}</Table.Td>
                  <Table.Td>{b.suid || ''}</Table.Td>
                  <Table.Td>{b.code || ''}</Table.Td>
                  <Table.Td>{b.name || ''}</Table.Td>
                  <Table.Td>{b.reason || ''}</Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="削除">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleDelete(b.id)}
                      >
                        <TrashIcon size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>
    </Box>
  )
}

export default BlacklistContainer
